// Column-sort state for a list whose rows are already in memory.
//
// `accessors` must be a stable reference — declare it as a module-level constant,
// not inline in the component, or the memo re-sorts on every render.

import { useMemo, useState } from 'react';
import { sortRows, type SortDir } from './tableSort';

export type SortAccessors<T, F extends string> = Record<F, (row: T) => unknown>;

export const useTableSort = <T, F extends string>(
  rows: T[],
  accessors: SortAccessors<T, F>,
  initialField: F | null = null,
  initialDir: SortDir = 'asc',
) => {
  const [sortField, setSortField] = useState<F | null>(initialField);
  const [sortDir, setSortDir] = useState<SortDir>(initialDir);

  // Clicking the active column flips it; a new column starts at A-Z, which is
  // what "sort by name" is asking for nine times out of ten.
  const onSort = (field: F) => {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortField(field);
    setSortDir('asc');
  };

  const sorted = useMemo(
    () => sortRows(rows, sortField ? accessors[sortField] : null, sortDir),
    [rows, accessors, sortField, sortDir],
  );

  return { sortField, sortDir, onSort, sorted };
};
